// components
import { capitalize, Tooltip } from '@mui/material';
import DutyCell from './DutyCell';

// hooks
import { useTranslation } from 'react-i18next';

// styles
import {
  StyledAbsenceBox,
  StyledDutyCellBox
} from '../hooks/useGetColumns.styles';

// types
import type { GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { EmployeeDay } from 'RestAPIs/cost-centers/employees/types';
import type { QualificationResponse } from 'RestAPIs/roster/qualifications/types';
import type { CostCentersFromOthersPrefValue } from '../hooks/useGetColumns.types';

// utils
import dayjs from 'dayjs';

/**
 * One day of one row: the duties of that date, with the absence markers around them.
 *
 * Internal and external absences render as separate bars above and below the duties — same day,
 * different marker — so both conditions are evaluated even though only the combined flag reaches
 * `DutyCell`.
 */
const DayCell = ({
  params,
  date,
  isOpenRequests = false,
  qualificationsByCostCenters = [],
  costCenterFromOthersPrefValue
}: {
  params: GridRenderCellParams;
  date: string;
  isOpenRequests?: boolean;
  qualificationsByCostCenters?: QualificationResponse[];
  costCenterFromOthersPrefValue?: CostCentersFromOthersPrefValue;
}) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });

  const days: EmployeeDay[] = params.row?.days || [];
  const absencesForTheDay =
    days.find((day: EmployeeDay) => dayjs(day.date).isSame(date, 'day'))
      ?.absences || [];

  const absencesInternal = absencesForTheDay?.filter(
    (absence) => !absence.flags.includes('EXTERNAL')
  );

  const absencesExternal = absencesForTheDay?.filter((absence) =>
    absence.flags.includes('EXTERNAL')
  );

  const isAbsentInternalToday = absencesInternal?.length > 0;

  const isAbsentExternalToday = absencesExternal?.length > 0;

  /**
   * A function rather than a value: `capitalize` throws on a non-string, and an open request row
   * has no `nameInformal`. The original only ever built this inside the two absence branches.
   */
  const absenceTooltip = () =>
    t('dataGrid.tooltip.employeeIsAbsent', {
      employee: capitalize(params.row.nameInformal)
    });

  return (
    <>
      {isAbsentInternalToday && (
        <Tooltip title={absenceTooltip()} arrow>
          <StyledAbsenceBox />
        </Tooltip>
      )}
      <StyledDutyCellBox>
        <DutyCell
          params={params}
          date={date}
          isOpenRequest={isOpenRequests}
          qualificationsByCostCenters={qualificationsByCostCenters}
          costCenterFromOthersPrefValue={costCenterFromOthersPrefValue}
          isAbsentToday={isAbsentInternalToday || isAbsentExternalToday}
        />
      </StyledDutyCellBox>
      {isAbsentExternalToday && (
        <Tooltip title={absenceTooltip()} arrow>
          <StyledAbsenceBox isExternal={isAbsentExternalToday} />
        </Tooltip>
      )}
    </>
  );
};

export default DayCell;
